import { WithPaginationApiResponseExtended } from 'types/pagination'
import { CommonSheet } from 'types/types'

export const EARN_ASSET_STATUS = {
  ACTIVE: 'ACTIVE',
  COMING_SOON: 'COMING_SOON',
} as const

export type EarnAssetStatus = (typeof EARN_ASSET_STATUS)[keyof typeof EARN_ASSET_STATUS]

export interface EarnAsset {
  asset: string
  portfolioId: string | null
  productId: string | null
  productName: string
  myEarnRate: number | null
  customerEarnRate: number | null
  earningSince: string | null
  status: EarnAssetStatus
  epcTakesEarn: boolean
}

export const EARN_DASHBOARD_STATE = {
  NO_PRODUCTS: 'NO_PRODUCTS',
  NO_WALLETS: 'NO_WALLETS',
  NO_PORTFOLIOS: 'NO_PORTFOLIOS',
  READY: 'READY',
} as const

export type EarnDashboardState = (typeof EARN_DASHBOARD_STATE)[keyof typeof EARN_DASHBOARD_STATE]

export interface EarnDashboardNoProducts {
  state: typeof EARN_DASHBOARD_STATE.NO_PRODUCTS
}

export interface EarnDashboardNoWallets {
  state: typeof EARN_DASHBOARD_STATE.NO_WALLETS
  featuredProduct: {
    currency: string
    earningRate: number
  }
}

export interface EarnDashboardNoPortfolios {
  state: typeof EARN_DASHBOARD_STATE.NO_PORTFOLIOS
  earnAssets: EarnAsset[]
}

export interface EarnDashboardData {
  state: typeof EARN_DASHBOARD_STATE.READY
  fundsOverview: {
    allocatedFunds: string
  }
  totalEarnings: {
    fromMyAllocation: string
    fromCustomers: string
    total: string
  }
  customerRewardsProgram: {
    distributedEarnings: string
    optedInTotal: string
    remainingTotal: string
  }
  earnAssets: EarnAsset[]
}

export type EarnDashboardResponse =
  | EarnDashboardNoProducts
  | EarnDashboardNoWallets
  | EarnDashboardNoPortfolios
  | EarnDashboardData

export interface EarnAssetDetailsResponse {
  asset: string
  walletId: string
  productName: string
  myEarnRate: number | null
  customerEarnRate: number | null
  fundsOverview: {
    allocatedFunds: string
    totalCustomerEligibleFunds: string
  }
  earningsSummary: {
    myLifetimeEarnings: string
    customerEarningsReceived: string
    distributedEarningsToCustomers: string
  }
}

export interface Money {
  amount: string
  currency: string
}

export interface MerchantStatistics {
  currency: string
  merchantMovableBalance: Money
  customerEarningBalance: Money
  optIn: {
    optedInCustomers: number
    totalCustomers: number
    rate: string
  }
}

export interface EarnStatisticsResponse {
  totals: {
    customerEarningBalance: string
    merchantMovableBalance: string
  }
  currencies: MerchantStatistics[]
}

export interface CreatePortfolioRequest {
  reference?: string
  earningProductId: string
}

export interface Counterparty {
  walletId: string
  amount: string
}

export interface DepositRequest {
  portfolioId: string
  reference: string
  originator: Counterparty
}

export const PortfolioStatus = {
  CREATED: 'CREATED',
  ACTIVE: 'ACTIVE',
  CLOSED: 'CLOSED',
} as const

export type PortfolioStatus = (typeof PortfolioStatus)[keyof typeof PortfolioStatus]

export interface Portfolio {
  id: string
  object: 'portfolio'
  reference?: string | null
  walletId?: string | null
  currency: string
  status: PortfolioStatus
  balance: string
  totalEarnedAmount: string
  createdAt: string
  updatedAt: string
}

export const TransferStatus = {
  PENDING: 'PENDING',
  COMPLETED_ON_LEDGER: 'COMPLETED_ON_LEDGER',
  SETTLED_WITH_PORTFOLIO: 'SETTLED_WITH_PORTFOLIO',
  FAILED: 'FAILED',
} as const

export type TransferStatus = (typeof TransferStatus)[keyof typeof TransferStatus]

export interface TransferAmount {
  amount: string
  currency: string
}

export interface DepositTransferResponse {
  id: string
  object: string
  reference: string
  status: TransferStatus
  portfolioId: string
  depositAmount: TransferAmount
  originatorWalletId: string
  balance: TransferAmount
  createdAt: string
  updatedAt: string
}

export interface EarnWalletsRequestParams {
  currencyCode?: string
  includeCustomerWallets?: boolean
  searchTerm?: string
  size?: number
  page?: number
}

export interface EarnWalletBalance {
  value: number
  currencyCode: string
}

export interface EarnWallet {
  id: string
  name: string
  status: string
  balance: EarnWalletBalance
}

export interface EarnWalletsResponse extends WithPaginationApiResponseExtended<EarnWallet[]> {}

export interface EarnAssetTransactionAmount {
  value: number
  currencyCode: string
}

export interface EarnAssetTransaction {
  transactionId: string
  paymentId: string
  paymentReference: string
  type: string
  status: string
  walletId: string
  amount: EarnAssetTransactionAmount
  runningBalance: EarnAssetTransactionAmount
  originator: unknown
  beneficiary: unknown
  details: unknown
  createdAt: string
  updatedAt?: string
}

export interface EarnAssetTransactionsResponse extends WithPaginationApiResponseExtended<
  EarnAssetTransaction[]
> {}

export interface EarnAssetTransactionsRequestParams {
  portfolioId?: string
  page?: number
  size?: number
  start?: string
  end?: string
}

export interface Wallet {
  value: string
  name: string
}

export const EARN_SHEET_ACTION_TYPE = {
  DEPOSIT: 'deposit',
  WITHDRAW: 'withdraw',
} as const

export type EarnSheetActionType =
  (typeof EARN_SHEET_ACTION_TYPE)[keyof typeof EARN_SHEET_ACTION_TYPE]

export interface EarnAction extends CommonSheet {
  type: EarnSheetActionType
  selectedCurrency?: string
  portfolioId?: string
  productName?: string
}

export interface EarnDepositFormData {
  currency: string
  allocateFrom: Wallet | null
  amount: string
  termsAccepted: boolean
}

export interface WithdrawalRequest {
  portfolioId: string
  reference: string
  walletId: string
  amount: string
}

export interface WithdrawalTransferResponse {
  id: string
  object: string
  reference: string
  status: TransferStatus
  portfolioId: string
  withdrawalAmount: TransferAmount
  beneficiaryWalletId: string
  balance: TransferAmount
  createdAt: string
  updatedAt: string
}

export interface EarnWithdrawFormData {
  currency: string
  withdrawToWallet: Wallet | null
  amount: string
}

export interface DistributionRequest {
  productId: string
  reference: string
  authenticationCode?: string
}

export interface EarnDistributionFormData {
  authenticationCode: string
}
